import { createSelector } from '@reduxjs/toolkit';

import type { ProductPromptType } from '@/components/products/ProductsPromptModal/redux/types';
import openSpeedyFlow from '@/components/SpeedyFlow/openSpeedyFlow';
import { isEmbedded, loadPage } from '@/helpers';
import route from '@/lib/route';
import { createSortedHabitatsSelector } from '@/redux/selectors/habitats';
import { createSelectCanAccessHabitat } from '@/redux/selectors/users';
import store from '@/redux/store';
import { selectAbility } from '@/shared/entitlements';

export const buildHabitatURL = (
  zooSlug: string,
  habitatSlug: string,
  query?: string | URLSearchParams,
  { skipPreview = true }: { skipPreview?: boolean } = {},
) => {
  const params = query instanceof URLSearchParams ? query : new URLSearchParams(query);
  // Signed-out visitors land on the Habitat Preview; default `timeline=0` to opt out.
  // A `ppt` prompt on the link opts out the same way.
  if (skipPreview && !store.getState().user.logged && !params.has('timeline') && !params.has('ppt')) {
    params.set('timeline', '0');
  }
  const queryString = params.toString();
  return `/h/${zooSlug}/${habitatSlug}${queryString ? `?${queryString}` : ''}`;
};

/**
 * Open a habitat as a whole new document rather than routing to it.
 *
 * For pages that are normally inside a frame: routing would swap the frame's contents and a plain
 * anchor would navigate the frame itself, so neither escapes it. `loadPage` targets the top level.
 */
export const loadHabitatPage = (zooSlug: string, habitatSlug: string, query?: string | URLSearchParams) =>
  loadPage(buildHabitatURL(zooSlug, habitatSlug, query));

export const selectHabitatForProductsPromptModal = createSelector(
  createSortedHabitatsSelector(['online', 'lastMediaCapture', 'index', 'currentViewers', 'title']),
  selectAbility,
  (habitats, ability) => habitats.find((habitat) => !ability.can('habitat.access', habitat._id)),
);

export const routeToHabitat = (
  {
    id,
    slug,
    zooSlug,
    query,
  }: {
    id: string;
    slug: string;
    zooSlug: string;
    query?: string;
  },
  ppt?: ProductPromptType,
) => {
  const state = store.getState();
  const canAccessHabitat = createSelectCanAccessHabitat(id)(state);
  const isLoggedIn = state.user.logged;
  if (isLoggedIn && !canAccessHabitat) {
    openSpeedyFlow(state, store.dispatch, { kind: 'habitat-access', habitatId: id, ppt, entryPoint: 'habitat-link' });
    return;
  }
  const url = buildHabitatURL(zooSlug, slug, query);
  if (isEmbedded()) {
    loadPage(url);
    return;
  }
  route(url);
};
